import React from 'react';
import { formatDateOnly } from '../../common';
import { SYSTEM_USER_ID } from '../../config';
import { useLocale } from '../../LocaleContext';
import MessageItem from './MessageItem';
import CallTimelineItem from '../call/CallTimelineItem';
import { buildChatTimeline } from '../../utils/callHistoryTimeline';
import { Messages, DateDivider } from '../../styles/UserWorkspacePageStyles';

const normalizeUtcDateString = (dateString) => {
  if (!dateString) return dateString;
  if (dateString.endsWith('Z') || dateString.includes('+') || dateString.match(/-\d{2}:\d{2}$/)) {
    return dateString;
  }
  return `${dateString}Z`;
};

const shouldGroupMessages = (earlierMsg, laterMsg) => {
  if (!earlierMsg || !laterMsg) return false;
  if (String(earlierMsg.sender_id).toLowerCase() !== String(laterMsg.sender_id).toLowerCase()) {
    return false;
  }

  const prevTime = new Date(normalizeUtcDateString(earlierMsg.created_at)).getTime();
  const currTime = new Date(normalizeUtcDateString(laterMsg.created_at)).getTime();
  if (Number.isNaN(prevTime) || Number.isNaN(currTime)) {
    return false;
  }

  return currTime - prevTime < 5 * 60 * 1000;
};

const isRenderableMessage = (msg) => {
  const hasFile = msg.attachments?.length > 0 && msg.message_type === 'file_message';
  const hasVoice = msg.message_type === 'voice_message' || msg.attachments?.[0]?.kind === 'voice';
  const hasText = msg.message && msg.message_type !== 'file_message' && msg.message_type !== 'voice_message' && msg.message.trim().length > 0;
  const isTyping = msg.online === 'true' && (msg.sender === 'system' || msg.sender_id === SYSTEM_USER_ID);
  return hasFile || hasVoice || hasText || isTyping;
};

const getPreviousRenderableMessage = (items, fromIndex) => {
  for (let i = fromIndex - 1; i >= 0; i -= 1) {
    if (items[i].type === 'date' || items[i].type === 'call') return null;
    if (isRenderableMessage(items[i])) return items[i];
  }
  return null;
};

const getNextRenderableMessage = (items, fromIndex) => {
  for (let i = fromIndex + 1; i < items.length; i += 1) {
    if (items[i].type === 'date' || items[i].type === 'call') return null;
    if (isRenderableMessage(items[i])) return items[i];
  }
  return null;
};

const MessageList = ({
  messages,
  callHistory = [],
  user,
  chats,
  selectedChatId,
  userAvatarUrl,
  peerAvatars,
  chatTrustPolicies,
  messagesContainerRef,
  messagesEndRef,
  onScroll,
  isLoading,
  onDeleteMessage,
  groupSenderNames,
}) => {
  const { locale } = useLocale();
  const findChatForMessage = (msg, chatsList, activeChatId) => {
    let chat = null;
    if (activeChatId) {
      const selectedChatIdStr = String(activeChatId);
      const msgChatIdStr = String(msg.chat_id || activeChatId);

      if (selectedChatIdStr === msgChatIdStr || !msg.chat_id) {
        chat = chatsList.find((c) => String(c.id || '') === selectedChatIdStr);
      }
    }

    if (!chat && msg.chat_id) {
      const msgChatIdStr = String(msg.chat_id);
      chat = chatsList.find((c) => String(c.id || '') === msgChatIdStr);
    }

    if (!chat && activeChatId) {
      const selectedChatIdStr = String(activeChatId);
      chat = chatsList.find((c) => String(c.id || '') === selectedChatIdStr);
    }

    return chat;
  };

  const groupedMessages = buildChatTimeline(messages, callHistory);
  const activeChat = chats.find((c) => String(c.id) === String(selectedChatId));
  const isGroupChat = activeChat?.chat_type === 'group';

  return (
    <Messages ref={messagesContainerRef} onScroll={onScroll} $isGroupChat={isGroupChat}>
      {groupedMessages.map((item, index) => {
        if (item.type === 'date') {
          return (
            <DateDivider key={item.id || `date-${item.date}-${index}-${locale}`}>
              {formatDateOnly(item.date)}
            </DateDivider>
          );
        }

        if (item.type === 'call') {
          return (
            <CallTimelineItem
              key={item.id || `call-${index}`}
              item={item.call}
            />
          );
        }

        const msg = item;
        const isUser = String(msg.sender_id).toLowerCase() === String(user.user_id).toLowerCase();
        const chat = findChatForMessage(msg, chats, selectedChatId);
        const previousItem = groupedMessages[index - 1];
        const previousRenderable = getPreviousRenderableMessage(groupedMessages, index);
        const nextRenderable = getNextRenderableMessage(groupedMessages, index);
        const isGrouped = previousRenderable ? shouldGroupMessages(previousRenderable, msg) : false;
        const isLastInGroup = !nextRenderable || !shouldGroupMessages(msg, nextRenderable);
        const isFirstAfterDate = previousItem?.type === 'date' || previousItem?.type === 'call';
        const isGroupChatMessage = chat?.chat_type === 'group';
        const showSenderName = isGroupChatMessage && !isUser && !isGrouped;
        const showAvatar = isGroupChatMessage && isLastInGroup;

        return (
          <MessageItem
            key={msg.id || `msg-${index}-${msg.created_at || Date.now()}`}
            msg={msg}
            isUser={isUser}
            user={user}
            chat={chat}
            userAvatarUrl={userAvatarUrl}
            peerAvatars={peerAvatars}
            chatTrustPolicies={chatTrustPolicies}
            selectedChatId={selectedChatId}
            onDeleteMessage={onDeleteMessage}
            showAvatar={showAvatar}
            showSenderName={showSenderName}
            isGrouped={isGrouped}
            isFirstAfterDate={isFirstAfterDate}
            groupSenderNames={groupSenderNames}
          />
        );
      })}
      {isLoading && <div>loading...</div>}
      <div ref={messagesEndRef} />
    </Messages>
  );
};

export default MessageList;
